import {useEffect, useRef, useState} from "react";
import {type GraphQLQuery} from "@aws-amplify/api-graphql";
import {useAmplifyClients} from "../AmplifyClientProvider";
import {withRetry} from "./retry";
import {
  AmplifyClientKey,
  GraphqlResult,
  GraphqlVariables,
  toError
} from "./types";

interface UseGraphqlQueryOptions<_TData, TVariables extends GraphqlVariables> {
  /** Amplify query string */
  amplifyQuery: string;
  /** Variables for the query */
  variables?: TVariables;
  /** Which Amplify client to use (defaults to "appsyncBff") */
  amplifyClient?: AmplifyClientKey;
  /** Skip execution */
  skip?: boolean;
}

/**
 * Amplify-only query hook returning { data, loading, error }.
 */
export function useGraphqlQuery<
  TData = unknown,
  TVariables extends GraphqlVariables = GraphqlVariables
>(options: UseGraphqlQueryOptions<TData, TVariables>): GraphqlResult<TData> {
  const clients = useAmplifyClients();
  const client = clients?.[options.amplifyClient ?? "appsyncBff"] ?? null;
  const [data, setData] = useState<TData | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (options.skip || !client) return;

    cancelledRef.current = false;
    setLoading(true);
    setError(undefined);

    const execute = async () => {
      try {
        const result = await withRetry(() =>
          client.graphql<GraphQLQuery<TData>>({
            query: options.amplifyQuery,

            variables: options.variables as any
          })
        );
        const resultData = result.data as TData;

        if (!cancelledRef.current) {
          setData(resultData);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelledRef.current) {
          setError(toError(err));
          setLoading(false);
        }
      }
    };

    execute();
    return () => {
      cancelledRef.current = true;
    };

  }, [options.skip, client, JSON.stringify(options.variables)]);

  return {data, loading, error};
}
